import React, { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { updateContactAttempt, ContactAttempt } from '~/services/contactAttempts';
import { toast } from 'react-hot-toast';

interface EditContactAttemptModalProps {
    isOpen: boolean;
    attempt: ContactAttempt | null;
    onClose: () => void;
    onSave: (updated: ContactAttempt) => void;
}

const METHOD_OPTIONS: { value: ContactAttempt['method']; label: string }[] = [
    { value: 'call', label: 'Call' },
    { value: 'email', label: 'Email' },
    { value: 'both', label: 'Call & Email' },
];

const EditContactAttemptModal: React.FC<EditContactAttemptModalProps> = ({
    isOpen,
    attempt,
    onClose,
    onSave,
}) => {
    const [noteContent, setNoteContent] = useState('');
    const [method, setMethod] = useState<ContactAttempt['method']>('call');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen && attempt) {
            setNoteContent(attempt.note || '');
            setMethod(attempt.method);
        }
    }, [isOpen, attempt?.id]);

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27) onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose]);

    const handleSave = async () => {
        if (!attempt?.id || isSaving) return;

        setIsSaving(true);

        const payload: Partial<{ note: string; method: string }> = {};
        if (noteContent !== (attempt.note || '')) {
            payload.note = noteContent;
        }
        if (method !== attempt.method) {
            payload.method = method;
        }

        if (Object.keys(payload).length === 0) {
            onClose();
            return;
        }

        try {
            const updated = await updateContactAttempt(attempt.id, payload);
            toast.success('Contact attempt updated');
            onSave(updated);
            onClose();
        } catch (error) {
            console.error('Error updating contact attempt:', error);
            toast.error('Failed to update contact attempt');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !attempt) return null;

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative max-h-[80vh] w-full max-w-2xl rounded-lg bg-white shadow-lg'
            >
                <div className='flex items-center justify-between p-6 pb-4'>
                    <h3 className='text-gray-900 text-[28px]/[32px] font-semibold'>
                        Edit Contact Attempt
                    </h3>
                    <button
                        className='text-gray-400 hover:text-gray-600 transition-colors'
                        onClick={onClose}
                        aria-label='Close modal'
                    >
                        <RxCross2 size={24} />
                    </button>
                </div>

                <div className='max-h-[60vh] overflow-y-auto px-6'>
                    <div className='mb-3'>
                        <label className='block text-xs font-semibold mb-1'>
                            Method
                        </label>
                        <div className='flex gap-3'>
                            {METHOD_OPTIONS.map((opt) => (
                                <label
                                    key={opt.value}
                                    className='flex items-center gap-1.5 text-sm cursor-pointer'
                                >
                                    <input
                                        type='radio'
                                        name='method'
                                        value={opt.value}
                                        checked={method === opt.value}
                                        onChange={() => setMethod(opt.value)}
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className='mb-3'>
                        <label className='block text-xs font-semibold mb-1'>
                            Note
                        </label>
                        <textarea
                            rows={3}
                            className='w-full rounded-md border border-black bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none'
                            placeholder='Add any notes about this contact attempt...'
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                        />
                    </div>
                </div>

                <div className='border-t border-gray-200 p-6'>
                    <div className='flex justify-start gap-3'>
                        <button
                            type='button'
                            onClick={onClose}
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            onClick={handleSave}
                            disabled={isSaving}
                            className='rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditContactAttemptModal;
